import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';

/**
 * Is this a mobile viewport? One answer, one listener, one breakpoint.
 *
 * Every shared component that cares about the viewport used to keep its own
 * `isMobile` field, its own `@HostListener('window:resize')` and its own copy of
 * `viewportService.isMobileView()` inside it — five of them (`table`, `filter`,
 * `filter-customizer`, `tabs`, `auth-form`). That is five listeners firing on
 * every pixel of a window drag to recompute the same boolean, and five places to
 * forget when the breakpoint moves.
 *
 * Two things changed:
 *
 * - **`matchMedia` instead of `resize`.** The media query fires only when the
 *   threshold is actually CROSSED, which is the only moment anybody cares about.
 *   Dragging a window used to run every listener on every frame.
 * - **`changes` for whoever needs the transition.** Most components only need to
 *   read the current value (a getter on this service is enough, and Angular
 *   re-evaluates it on change detection). `table` is the exception: it has to
 *   collapse expanded rows and move the page size 10 ↔ 8 when the viewport
 *   *becomes* mobile, and that is a transition, not a value.
 */
@Injectable({
	providedIn: 'root',
})
export class ViewportService implements OnDestroy {
	/** Under this width the UI switches to its mobile layout. */
	static readonly MOBILE_BREAKPOINT = 1024;

	private readonly query = window.matchMedia(`(max-width: ${ViewportService.MOBILE_BREAKPOINT - 1}px)`);
	private readonly mobileChanges = new Subject<boolean>();
	private mobile: boolean;

	private readonly onChange = (event: MediaQueryListEvent) => {
		this.mobile = event.matches;
		this.mobileChanges.next(this.mobile);
	};

	constructor() {
		this.mobile = this.query.matches;
		this.query.addEventListener('change', this.onChange);
	}

	ngOnDestroy(): void {
		this.query.removeEventListener('change', this.onChange);
		this.mobileChanges.complete();
	}

	/**
	 * Whether the current viewport is mobile. Safe to call from a template: it
	 * reads a field, it does not measure the window.
	 */
	isMobileView(): boolean {
		return this.mobile;
	}

	/**
	 * Emits ONLY when the viewport crosses the breakpoint, with the new value.
	 * For components that must react to the change itself and not just read it.
	 */
	get changes(): Observable<boolean> {
		return this.mobileChanges.asObservable();
	}
}
